import React, { useEffect, useRef } from 'react';
import { useDesignTheme } from '../../hooks/useDesignTheme';

interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
}

export function ParticleBackground() {
  const { theme, isDark } = useDesignTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    // Initial resize
    handleResize();

    let particlesArray: Particle[] = [];

    // Create initial particles
    const particleCount = window.innerWidth < 768 ? 30 : 50;
    for (let i = 0; i < particleCount; i++) {
      particlesArray.push({
        id: i,
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 1
      });
    }

    let animationFrameId: number;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particlesArray.forEach((particle, i) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        // Attract to mouse
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 150) {
          particle.vx += dx * 0.00005;
          particle.vy += dy * 0.00005;
        }

        // Draw particle
        ctx.fillStyle = theme.particle;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();

        // Draw connections
        particlesArray.slice(i + 1).forEach(particle2 => {
          const dx2 = particle.x - particle2.x;
          const dy2 = particle.y - particle2.y;
          const distance2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

          if (distance2 < 100) {
            const baseOpacity = isDark ? 0.2 : 0.2;
            // Replace opacity in rgba string
            // Assuming theme.particleConnection is like 'rgba(..., ..., ..., 0.2)'
            // We need to construct the color string dynamically
            // But the figma code did: theme.particleConnection.replace('0.2', ...)
            // Let's try to be robust or just follow the figma logic if the string format matches
            
            let strokeStyle = theme.particleConnection;
            if (strokeStyle.includes('0.2')) {
                 strokeStyle = strokeStyle.replace('0.2', String(baseOpacity * (1 - distance2 / 100)));
            } else {
                // Fallback if the string doesn't contain 0.2 exactly as expected
                // This is a bit hacky but follows the figma logic
                // A better way would be to parse the color
                 strokeStyle = strokeStyle.replace(/[\d.]+\)$/, `${String(baseOpacity * (1 - distance2 / 100))})`);
            }

            ctx.strokeStyle = strokeStyle;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(particle2.x, particle2.y);
            ctx.stroke();
          }
        });
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [theme, isDark]);

  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />;
}
